/**
 * The OpenFreeMap basemap styles compare feature properties with numbers in
 * their filters: `ref_length <= 6` on the road shields, `rank` on point labels,
 * `admin_level` on boundaries. A feature without the property (most Polish
 * roads have no `ref`) makes MapLibre log "Expected value to be of type number,
 * but found null instead." from every tile worker. The guard gives each such
 * property read a default that keeps the comparison false, which is what
 * MapLibre did anyway after the warning, so the map looks the same.
 */
const ORDERING = ['<', '<=', '>', '>='];
// Far outside every value the styles compare against.
const OUT_OF_RANGE = 1e9;

export function guardNullComparisons(expression) {
    if (!Array.isArray(expression) || expression[0] === 'literal') {
        return expression;
    }
    const guarded = expression.map((part, index) => (index === 0 ? part : guardNullComparisons(part)));
    if (!ORDERING.includes(guarded[0]) || guarded.length < 3) {
        return guarded;
    }
    const lessThan = guarded[0] === '<' || guarded[0] === '<=';
    [1, 2].forEach((position) => {
        const operand = guarded[position];
        if (!Array.isArray(operand) || operand[0] !== 'get' || typeof guarded[3 - position] !== 'number') {
            return;
        }
        // A missing left operand must not be below (for < and <=) or above
        // (for > and >=) the number; a missing right operand the reverse.
        const fallback = lessThan === (position === 1) ? OUT_OF_RANGE : -OUT_OF_RANGE;
        guarded[position] = ['coalesce', operand, fallback];
    });
    return guarded;
}

function guardProperties(properties) {
    if (!properties || typeof properties !== 'object') {
        return properties;
    }
    const guarded = {};
    Object.keys(properties).forEach((name) => {
        guarded[name] = guardNullComparisons(properties[name]);
    });
    return guarded;
}

export function guardBasemapStyle(style) {
    if (!style || !Array.isArray(style.layers)) {
        return style;
    }
    return {
        ...style,
        layers: style.layers.map((layer) => {
            const guarded = { ...layer };
            if (layer.filter) guarded.filter = guardNullComparisons(layer.filter);
            if (layer.layout) guarded.layout = guardProperties(layer.layout);
            if (layer.paint) guarded.paint = guardProperties(layer.paint);
            return guarded;
        }),
    };
}

/** Options for map.setStyle(): applies the guard to the fetched style. */
export const basemapStyleOptions = {
    transformStyle: (previous, next) => guardBasemapStyle(next),
};

/**
 * `new maplibregl.Map(options)` with the style loaded through the guard. Only
 * setStyle() accepts the transform, so the style is set right after the
 * constructor, before the first frame requests any tile.
 */
export function createBasemapMap(maplibregl, options) {
    const { style, ...mapOptions } = options;
    const map = new maplibregl.Map(mapOptions);
    if (style) map.setStyle(style, basemapStyleOptions);
    return map;
}
